// =================================================================================================
// Template USAGE:
//  -- 3 files (WidgetTemplate, WidgetTemplateAsync, WidgetTemplateStructs)
//  - Update paths of internal components
//  - Replace WidgetTemplate to end widget name
//  - Update WidgetTemplateData with widget settings
//  - Update WidgetTemplateSettings with mapping of this settings in Settings Group
//  - Update ports and things information
//  - Set default values for widget data
//  - Register the widget to factory
//  - Add widget to index.ts file to be pe part of your app.js
// =================================================================================================

// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType, StateStatus } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { LazyLoadingAnimation } from '@Primitive/Lazy';

import { PortKeys, WidgetTemplateData } from '../WidgetTemplateStructs';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';

interface IFrameWidgetData { }
// ------------------------------------------------------------------------------------------------------
const IFrameSettings = React.lazy(() => import("../WidgetTemplateAsync").then(module => ({ default: module.WidgetTemplateSettings })));

//Validator
// -------------------------------------------------------------------------------------------------------
export function IFrameSchemeValidator() {
    return JsonSchemaValidator.GetInstance(
        "IFrameSchemeValidator",
        {
            type: "object",
            properties: {
                src: {
                    type: "string"
                },
                title: {
                    type: "string"
                },
                sandbox: {
                    type: "string"
                },
            },
            required: [
                "src",
                "title",
                "sandbox"
            ]
        });
}
// -------------------------------------------------------------------------------------------------------
export interface IFrameData {
    src: string;
    title: string;
    sandbox: string;
}
// -------------------------------------------------------------------------------------------------------
export interface IFrameState {
}
// -------------------------------------------------------------------------------------------------------
export class IFrame extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<IFrameData>, IFrameState> {
    // ---------------------------------------------------------------------------------------------------
    render() {
        if (_.has(this.props.PortsStates, PortKeys.Data) &&
            this.props.PortsStates[PortKeys.Data].LastStateStatus == StateStatus.Valid) {
            let { src, title, sandbox } = this.props.PortsStates[PortKeys.Data].State;

            return <iframe
                style={{ height: this.props.height, width: this.props.width, position: 'absolute', border: "unset" }}
                src={src}
                title={title}
                sandbox={sandbox}>
            </iframe>
        }
        else
            return null;
    }
}
// =================================================================================================
export class IFrameWidget extends IWidgetThingsClass<IFrameWidgetData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <IFrame />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <IFrameSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ---------------------------------------------------------------------------------------------


    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: IFrameWidgetData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: "",
                State: ""
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        console.log("this.Description-Iframe->", this.Description)
        let widgetData = (this.WidgetData as IFrameWidgetData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): IFrameWidgetData {
        return {
            // TODO: Set the default values for the widget
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return IFrameWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description

        return {
            Title: 'IFrameWidget',
            WidgetTitle: '',
            Key: 'IFrameWidget',
            Type: 'IFrameWidget',
            Data: IFrameWidget.GetDefaultWidgetDataStatic(),
            Description: 'IFrameWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data)
            return IFrameSchemeValidator().DataValidateString(value);
        return { valid: true, errors: '' };
        // todo: set schema validators
        //return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: WidgetTemplateData): WidgetTemplateData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}